import * as React from 'react';
import { createTheme, ThemeOptions } from '@mui/material';
import shadows from '@mui/material/styles/shadows';

import {
  black,
  blue,
  brand,
  green,
  grey,
  purple,
  red,
  slate,
  ui,
  white,
  yellow,
} from '../styles/colors';
import { primaryFontFamily } from '../styles/fonts';

declare module '@mui/material' {
  interface TypographyVariants {
    font11: React.CSSProperties;
    font12: React.CSSProperties;
    font12Medium: React.CSSProperties;
    font14: React.CSSProperties;
    font14Bold: React.CSSProperties;
    font14Button: React.CSSProperties;
    font14Medium: React.CSSProperties;
    font16: React.CSSProperties;
    font16Bold: React.CSSProperties;
    font16Medium: React.CSSProperties;
    font18: React.CSSProperties;
    font18Bold: React.CSSProperties;
    font20: React.CSSProperties;
    font20Medium: React.CSSProperties;
    font24: React.CSSProperties;
    font28: React.CSSProperties;
    font32: React.CSSProperties;
    font36: React.CSSProperties;
    font42: React.CSSProperties;
    font46: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    font11?: React.CSSProperties;
    font12?: React.CSSProperties;
    font12Medium?: React.CSSProperties;
    font14?: React.CSSProperties;
    font14Bold?: React.CSSProperties;
    font14Button?: React.CSSProperties;
    font14Medium?: React.CSSProperties;
    font16?: React.CSSProperties;
    font16Bold?: React.CSSProperties;
    font16Medium?: React.CSSProperties;
    font18?: React.CSSProperties;
    font18Bold?: React.CSSProperties;
    font20?: React.CSSProperties;
    font20Medium?: React.CSSProperties;
    font24?: React.CSSProperties;
    font28?: React.CSSProperties;
    font32?: React.CSSProperties;
    font36?: React.CSSProperties;
    font42?: React.CSSProperties;
    font46?: React.CSSProperties;
  }
}
// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    font11: true;
    font12: true;
    font12Medium: true;
    font14: true;
    font14Bold: true;
    font14Button: true;
    font14Medium: true;
    font16: true;
    font16Bold: true;
    font16Medium: true;
    font18: true;
    font18Bold: true;
    font20: true;
    font20Medium: true;
    font24: true;
    font28: true;
    font32: true;
    font36: true;
    font42: true;
    font46: true;
  }
}
// Extend the palette interface module to contain our color set
export type ColorStrengths =
  | 100
  | 200
  | 300
  | 400
  | 500
  | 600
  | 700
  | 800
  | 900;
type ColorStrengthsExtra = 50 | ColorStrengths;
interface Brand {
  brightBlue: string;
  darkGrey: string;
  deepBlue: string;
  deepPink: string;
  deepPurple: string;
  grey: string;
  greyBackground: string;
  greyText: string;
  magenta: string;
  orange: string;
  paleBlue: string;
  pastelGreen: string;
  pastelPink: string;
  pink: string;
  purple: string;
  teal: string;
  yellow: string;
}

type ComplexColor = Record<ColorStrengths, string>;
type ComplexColorExtra = Record<ColorStrengthsExtra, string>;
declare module '@mui/material/styles' {
  interface Palette {
    colors: {
      black: string;
      blue: ComplexColorExtra;
      brand: Brand;
      green: ComplexColor;
      grey: ComplexColor;
      purple: ComplexColor;
      red: ComplexColor;
      slate: ComplexColor;
      white: string;
      yellow: ComplexColor;
    };
  }
  interface PaletteOptions {
    colors: {
      black: string;
      blue: ComplexColorExtra;
      brand: Brand;
      green: ComplexColor;
      grey: ComplexColor;
      purple: ComplexColor;
      red: ComplexColor;
      slate: ComplexColor;
      white: string;
      yellow: ComplexColor;
    };
  }
}

const BackgroundToTextColor = {
  [purple[500]]: white,
  [purple[600]]: white,
  [purple[100]]: purple[500],
  [purple[200]]: purple[200],
  white: purple[500],
  [red[500]]: white,
  [red[600]]: white,
};

const themeOptions: ThemeOptions = {
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiInputLabel: {
      defaultProps: {
        shrink: true,
      },
    },
    MuiModal: {
      defaultProps: {
        container: undefined,
      },
    },
    MuiOutlinedInput: {
      defaultProps: {
        notched: false,
      },
    },
    MuiPopover: {
      defaultProps: {
        container: undefined,
      },
    },
    MuiPopper: {
      defaultProps: {
        container: undefined,
      },
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
        componentsProps: {
          popper: {
            sx: [
              {
                '& .MuiTooltip-arrow': {
                  color: grey[800],
                },
                '& .MuiTooltip-tooltip': {
                  backgroundColor: grey[800],
                  fontSize: '14px',
                  padding: '8px 12px',
                  whiteSpace: 'pre-line',
                },
              },
            ],
          },
        },
      },
    },
  },

  palette: {
    action: {
      disabledBackground: slate[100],
    },
    colors: {
      black,
      blue,
      brand,
      green,
      grey,
      purple,
      red,
      slate,
      white,
      yellow,
    },
    error: {
      main: ui.semantic.destructive.default,
    },
    getContrastText: (background: string) => BackgroundToTextColor[background],
    info: {
      main: purple[500],
    },
    primary: {
      main: purple[500],
    },
    secondary: {
      contrastText: purple[500],
      main: purple[100],
    },
    success: {
      main: green[500],
    },
    text: {
      disabled: ui.text.disabled,
      primary: ui.text.primary,
    },
    warning: {
      main: ui.semantic.warning.default,
    },
  },
  // @ts-expect-error TypeScript incorrectly thinks `shadows` can have less than 25 items
  shadows: [
    'none',
    '0px 2px 6px 0px rgba(3, 17, 38, 0.11), 0px 0px 1px 0px rgba(0, 12, 32, 0.04)',
    '2px 5px 8px 0px rgba(3, 17, 38, 0.11), 0px 0px 1px 0px rgba(0, 12, 32, 0.04)',
    '2px 8px 16px 0px rgba(3, 17, 38, 0.11), 0px 0px 1px 0px rgba(0, 12, 32, 0.02)',
    '4px 12px 20px 0px rgba(3, 17, 38, 0.11), 0px 0px 1px 0px rgba(0, 12, 32, 0.02)',
    '6px 16px 24px 0px rgba(3, 17, 38, 0.11), 0px 0px 1px 0px rgba(0, 12, 32, 0.02)',
    ...shadows.slice(6),
  ],
  typography: {
    button: {
      textTransform: 'none',
    },
    font11: {
      fontSize: '11px',
      fontWeight: 600,
      lineHeight: '14px',
    },
    font12: {
      fontSize: 12,
      fontWeight: 400,
      lineHeight: '18px',
    },
    font12Medium: {
      fontSize: 12,
      fontWeight: 500,
      lineHeight: '18px',
    },
    font14: {
      fontSize: 14,
      fontWeight: 400,
      lineHeight: '20px',
    },
    font14Bold: {
      fontSize: 14,
      fontWeight: 600,
      lineHeight: '20px',
    },
    font14Button: {
      fontSize: 14,
      fontWeight: 500,
      lineHeight: '20px',
    },
    font14Medium: {
      fontSize: 14,
      fontWeight: 500,
      lineHeight: '20px',
    },
    font16: {
      fontSize: 16,
      fontWeight: 400,
      lineHeight: '22px',
    },
    font16Bold: {
      fontSize: 16,
      fontWeight: 600,
      lineHeight: '22px',
    },
    font16Medium: {
      fontSize: 16,
      fontWeight: 500,
      lineHeight: '22px',
    },
    font18: {
      fontSize: 18,
      fontWeight: 500,
      lineHeight: '26px',
    },
    font18Bold: {
      fontSize: 18,
      fontWeight: 600,
      lineHeight: '26px',
    },
    font20: {
      fontSize: 20,
      fontWeight: 600,
      lineHeight: '20px',
    },
    font20Medium: {
      fontSize: 20,
      fontWeight: 500,
      lineHeight: '28px',
    },
    font24: {
      fontSize: 24,
      fontWeight: 600,
      lineHeight: '30px',
    },
    font28: {
      fontSize: 28,
      fontWeight: 600,
      lineHeight: '28px',
    },
    font32: {
      fontSize: 32,
      fontWeight: 600,
      lineHeight: '32px',
    },
    font36: {
      fontSize: 36,
      fontWeight: 600,
      lineHeight: '40px',
    },
    font42: {
      fontSize: 42,
      fontWeight: 600,
      lineHeight: '42px',
    },
    font46: {
      fontSize: 46,
      fontWeight: 600,
      lineHeight: '42px',
    },
    fontFamily: primaryFontFamily,
  },
};

const theme = createTheme(themeOptions);

export const createRootedTheme = (newRoot: HTMLDivElement | null) => {
  // merge this new theme with the theme and return it, then use it in themewrapper
  const newTheme: ThemeOptions = {
    ...themeOptions,
    components: {
      ...themeOptions.components,
      MuiModal: {
        defaultProps: {
          container: newRoot,
        },
      },
      MuiPopover: {
        defaultProps: {
          container: newRoot,
        },
      },
      MuiPopper: {
        defaultProps: {
          container: newRoot,
        },
      },
    },
    zIndex: {
      ...themeOptions.zIndex,
      modal: 909090,
      snackbar: 909191,
      tooltip: 909292,
    },
  };

  return createTheme(newTheme);
};

/**
 * @deprecated Do not import this module directly. Instead, use a theme provider or `import { useTheme } from '@mui/material/styles';`
 */
export default theme;
